import { Link } from "react-router-dom";
import { ArrowRight, Leaf, Clock, HeartHandshake } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import SectionHeading from "../components/SectionHeading";
import CTABanner from "../components/CTABanner";
import { useSEO } from "../hooks/useSEO";
import { aboutImage, aboutImageAlt } from "../data/realisations";
import { advantages } from "../data/services";

const values = [
  {
    icon: Leaf,
    title: "Soin & précision",
    text: "Chaque détail compte : coupe nette, bordures propres, espaces laissés impeccables après chaque intervention.",
  },
  {
    icon: Clock,
    title: "Sérieux & ponctualité",
    text: "Nous venons à l'heure convenue et menons chaque chantier avec méthode et régularité.",
  },
  {
    icon: HeartHandshake,
    title: "Proximité client",
    text: "Un interlocuteur à votre écoute, des conseils adaptés et un suivi personnalisé dans la durée.",
  },
];

export default function About() {
  useSEO({
    title: "À propos — Embèk Group, entreprise de jardinage et espaces verts",
    description:
      "Embèk Group, votre entreprise de jardinage : transformation, entretien et valorisation de vos espaces extérieurs avec sérieux, soin et savoir-faire.",
  });

  return (
    <>
      <PageHeader
        eyebrow="À propos"
        title="Au service de vos espaces verts"
        description="Une équipe passionnée, un travail soigné, des clients sereins : voilà notre quotidien."
      />

      <section className="py-16 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <Reveal className="relative">
            <div className="rounded-t-[10rem] rounded-b-3xl overflow-hidden h-[420px] lg:h-[560px] shadow-2xl shadow-forest/15">
              <img src={aboutImage} alt="Jardinier Embèk Group au travail" className="w-full h-full object-cover" />
            </div>
            <div className="hidden sm:block absolute -bottom-8 -right-4 w-48 h-48 rounded-3xl overflow-hidden border-8 border-sand shadow-xl">
              <img src={aboutImageAlt} alt="Pelouse entretenue" className="w-full h-full object-cover" />
            </div>
          </Reveal>
          <div>
            <SectionHeading eyebrow="Notre histoire" title="Embèk Group, au service de vos espaces verts" />
            <Reveal delay={0.15}>
              <p className="mt-6 text-coal-muted font-light leading-relaxed">
                Chez Embèk Group, nous avons à cœur de transformer, entretenir et valoriser vos espaces extérieurs.
                Notre objectif est de proposer un travail professionnel, soigné et adapté à chaque client.
              </p>
              <p className="mt-4 text-coal-muted font-light leading-relaxed">
                Qu'il s'agisse de l'entretien régulier d'un jardin, de la taille d'une haie, de la création d'un nouvel
                espace vert ou de la remise en état d'un terrain, nous vous accompagnons avec sérieux et savoir-faire.
              </p>
              <p className="mt-4 text-coal-muted font-light leading-relaxed">
                Professionnalisme, ponctualité et attention au détail guident chacune de nos interventions. Notre
                récompense : des extérieurs impeccables où vous prenez plaisir à vivre et à recevoir.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <Link to="/demander-un-devis" data-testid="about-devis-button" className="btn-primary mt-9">
                Parlons de votre projet <ArrowRight size={15} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-sand-mid py-16 lg:py-24" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <SectionHeading align="center" eyebrow="Nos valeurs" title="Ce qui guide chaque intervention" />
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08} className="h-full">
                <div className="h-full bg-white rounded-3xl border border-forest/10 p-8 text-center shadow-sm hover:shadow-lg hover:shadow-forest/10 transition-shadow">
                  <span className="mx-auto w-14 h-14 rounded-full bg-forest/8 text-moss flex items-center justify-center">
                    <v.icon size={24} />
                  </span>
                  <h3 className="mt-5 font-serif text-2xl text-forest">{v.title}</h3>
                  <p className="mt-3 text-sm text-coal-muted font-light leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {advantages.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.06}>
                <p className="text-center font-serif italic text-lg text-forest/80">— {a.title}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABanner />
    </>
  );
}
